import { Loading } from "@admin/components/atoms/Loading";
import {
  DataTable,
  DataTableColumn,
} from "@admin/components/molecules/Table/DataTable";
import { useCurrentUserGroups } from "@admin/hooks/useCurrentUserGroups";
import {
  getRiskAnalysisByLocationId,
  RiskAnalysisModel,
  RiskAnalysisRowData,
  RiskAnalysisTableData,
  riskAnalysisTableData,
  riskAnalysisTableMap,
  updateRiskAnalysis,
} from "@enos-monorepo/lib";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface RiskAnalysisProps {
  locationId?: string;
}

const columns: DataTableColumn<RiskAnalysisRowData>[] = [
  {
    header: "Risici",
    accessorKey: "risk",
    size: 175,
    headerFontSize: 18,
    headerFontWeight: 600,
    headerColor: "black",
    cell: ({ row }) => {
      return (
        <span className="text-black font-semibold">{row.original.risk}</span>
      );
    },
  },
  {
    header: "CCP \n / GAG",
    accessorKey: "CCPorGAG",
    headerFontSize: 18,
    headerFontWeight: 600,
    headerColor: "black",
    size: 100,
    cell: ({ row }) => {
      return (
        <span className="text-black font-semibold">
          {row.original.CCPorGAG}
        </span>
      );
    },
  },
  {
    header: "",
    accessorKey: "general",
    headerFontSize: 18,
    headerFontWeight: 600,
    headerColor: "black",
    size: 200,
    cell: ({ row }) => {
      return (
        <span className="text-black font-semibold">{row.original.general}</span>
      );
    },
  },
  {
    header: "Beskrivelse af Risici",
    accessorKey: "description",
    headerFontSize: 18,
    headerFontWeight: 600,
    headerColor: "black",
    cell: ({ row }) => {
      return (
        <span className="text-black font-normal">
          {row.original.description}
        </span>
      );
    },
  },
  {
    header: "Styrende foranstaltninger for at 'undgå/forebygge'",
    accessorKey: "prevent",
    headerFontSize: 18,
    headerFontWeight: 600,
    headerColor: "black",

    cell: ({ row }) => {
      return (
        <span className="text-black font-normal">{row.original.prevent}</span>
      );
    },
  },
];

export function RiskAnalysis({ locationId }: RiskAnalysisProps) {
  const { t } = useTranslation();
  const { isSuperAdmin } = useCurrentUserGroups();
  const [selectedRowsByTable, setSelectedRowsByTable] = useState<
    Record<string, RiskAnalysisRowData[]>
  >({});

  const queryClient = useQueryClient();

  const { data: riskAnalysis, isPending } = useQuery({
    queryKey: ["riskAnalysis", locationId],
    queryFn: () => getRiskAnalysisByLocationId(locationId as string),
    enabled: !!locationId,
  });

  const mutation = useMutation({
    onMutate: async (data) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ["riskAnalysis"] });

      // Snapshot the previous value
      const previousRiskAnalysis = queryClient.getQueryData([
        "riskAnalysis",
        locationId,
      ]);

      // Optimistically update to the new value
      queryClient.setQueryData(
        ["riskAnalysis", locationId],
        (old: RiskAnalysisModel) => ({
          ...old,
          ...data,
        }),
      );

      // Return a context object with the snapshotted value
      return { previousRiskAnalysis };
    },
    mutationFn: updateRiskAnalysis,
    onSuccess: () => {
      toast.success(t("common.The changes have been saved"), {
        duration: 4000,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["riskAnalysis"] });
    },
    onError: (err, newTodo, context) => {
      queryClient.setQueryData(
        ["riskAnalysis", locationId],
        context?.previousRiskAnalysis,
      );
      toast.error(t("common.Unable to save the changes"), {
        duration: 4000,
      });
    },
  });

  const handleRowChange = (
    tableKey: string,
    selectedRows: RiskAnalysisRowData[],
  ) => {
    if (!riskAnalysis) return;

    setSelectedRowsByTable((prev) => ({
      ...prev,
      [tableKey]: selectedRows, // Sadece ilgili tablonun seçili satırlarını güncelle
    }));

    const updatedData: Record<string, boolean> = Object.fromEntries(
      Object.keys(riskAnalysis)
        .filter((id) => riskAnalysisTableMap[id] === tableKey)
        .filter(
          (key) => !["id", "companyId", "companyLocationId"].includes(key),
        )
        .map((key) => [key, false]),
    );

    // Seçilen satırları true yap
    selectedRows.forEach((row) => {
      if (!row) return;
      updatedData[row.id] = true;
    });

    if (selectedRowsByTable[tableKey]) {
      selectedRowsByTable[tableKey].forEach((prevRow) => {
        if (!selectedRows.find((row) => row.id === prevRow.id)) {
          updatedData[prevRow.id] = false;
        }
      });
    }

    mutation.mutate({
      ...riskAnalysis,
      ...updatedData,
    });
  };

  return (
    <div className="flex flex-col gap-7.5">
      {isPending ? (
        <Loading />
      ) : (
        riskAnalysis &&
        riskAnalysisTableData.map((table: RiskAnalysisTableData) => {
          if (
            !isSuperAdmin &&
            Object.values(table.data).filter((row) => {
              return riskAnalysis[row.id as keyof RiskAnalysisModel] === true;
            }).length === 0
          ) {
            return null;
          }

          return (
            <DataTable
              key={table.title}
              title={table.title}
              columns={columns}
              data={
                isSuperAdmin
                  ? Object.values(table.data)
                  : Object.values(table.data).filter((row) => {
                      return riskAnalysis[row.id as keyof RiskAnalysisModel];
                    })
              }
              titleFontSize="xl"
              isInfoIcon={false}
              rowSelect={isSuperAdmin}
              defaultSelectedRows={Object.entries(riskAnalysis)
                .filter(([id]) => riskAnalysisTableMap[id] === table.title)
                .filter(([, value]) => value)
                .map(([id]) => table.data[id])}
              onRowsSelectChange={(selectedRows) =>
                handleRowChange(table.title, selectedRows)
              }
            />
          );
        })
      )}
    </div>
  );
}
